/**
 * The two decisions behind the workspace explorer's "Share link…" item, as
 * functions rather than expressions inside a 3,000-line render.
 *
 * They are here because nothing in the repo renders `WorkspaceExplorer` in a
 * unit test, and a review proved what that costs: dropping the whole gate,
 * dropping the race guard, and making the item disappear entirely each left the
 * suite green. Pulled out, both are ordinary functions with ordinary tests.
 */
import { isPendingPointer, type PendingPointerLike } from '@papan/files/ui/pending-pointer-gate'
import { structuralFolderKind, structuralFolderBlocksShare, isChainPointerRow } from '@papan/types'

/** A `.chain` whose target is gone — kind AND the flag, like `isPendingPointer`
 *  (I110: the ending alone never decides). The server only ever sets the flag on a
 *  chain, so this is consistency rather than a live difference. */
function isDeadChain(f: PendingPointerLike): boolean {
  return isChainPointerRow(f) && !!f.chainBroken
}

/** The right-clicked row, as the context menu records it. */
export interface ShareLinkRow {
  itemId?: string
  itemName?: string
  itemIsFolder?: boolean
  itemPointer?: PendingPointerLike
  itemReshareBlocked?: boolean
  /** The pointer is dead — a mint on it is a hard 403. See below. */
  itemShareBroken?: boolean
}

/**
 * Whether to offer "Share link…" on this row.
 *
 * Not gated on an org the way "Who has access…" is: a link is how you reach
 * someone who is not in your org at all, which is exactly the personal-space
 * case. What it refuses:
 *
 * - **The PDApps container**, which cannot be shared whole. The drive decides
 *   that from a row's full drive-root path; this tree has none, so the NAME is
 *   read as if top-level — which catches `pdapps` and cannot catch `drive-root`
 *   (that needs an empty path, and the root is never a row here). The cost is
 *   over-blocking a NESTED folder named like a reserved root: a missing menu
 *   item, never a share that should not have been offered. The server would NOT
 *   refuse that nested lookalike — it walks real ancestors — so this is the only
 *   place the decision gets made, and it errs towards offering less.
 * - **A `.chain` still awaiting approval**: links do not follow one (D17), so
 *   the link would mint and serve nothing.
 * - **A pointer shared to this org without onward-sharing rights**: the mint is
 *   refused (`reshare_blocked_by_grant`), and the drive has greyed its share
 *   actions on this since #327 — whose comment says why: "Previously this stayed
 *   live and the mint 403'd — the UI offered an action that could never
 *   succeed." The first version of this item put that back, believing the tree's
 *   rows did not carry the flag; they did not, because the row mapping dropped
 *   it. Hidden rather than disabled-with-a-reason because this menu has no
 *   tooltip channel; the drive's row does.
 * - **A DEAD pointer** — a revoked or targetless `.shared` (`shareBroken`), or a
 *   `.chain` whose target is gone (`chainBroken`). MEASURED, twice, after two
 *   rounds argued about it from the UI alone: `POST /api/share-links/quick-view`
 *   on one answers a hard `403 no_file_access`. Not "a link that serves nothing".
 *   So this is #327 exactly — "the UI offered an action that could never succeed".
 *
 *   The gate is `guardCallerCapabilities` → `effectiveSharePermission`, which
 *   resolves through `resolveMountFile` and then `resolutionToPermission`, landing
 *   below the `readcopy` the mint requires.
 *
 *   **The difference is which ID is asked about, not which function.**
 *   `effectiveSharePermission` resolves THROUGH the pointer, so a dead one lands at
 *   `'none'`; `resolveFilePermission` asked about the POINTER ROW answers `owner`,
 *   because the row itself is yours. Reading that second answer is how a reader
 *   talks themselves into offering the item again. (An earlier version of this note said
 *   `resolveFilePermission` was "on the same route". It is not on `/quick-view` at
 *   all, and sending a reader to grep a route with nothing in it is its own way of
 *   making this note look confused.)
 *
 *   Nor are the two shapes the same underneath: a dead `.shared` resolves
 *   `{kind:'broken'}`, while a dead `.chain` resolves `{kind:'ok', permission:'none'}`
 *   — `resolutionToPermission` flattens both to `'none'` ("broken ⇒ no access for any
 *   gate that needs a FilePermission").
 *
 *   And the dialog cannot be left to discover this for itself: opening it calls
 *   `POST /default`, whose only file check is that `resolveFilePermission` on the
 *   pointer row — so it answers **201** on a dead pointer. The reader would get a
 *   normal-looking dialog with a link in it and only hit the 403 on adding a
 *   recipient. `isPendingPointer` does not cover any of this — a broken chain is no
 *   longer "awaiting approval".
 *
 *   The drive's ⋮ menu used to offer it while its own hover quick-action refused
 *   the same row, so the two halves disagreed with each other; it refuses now too
 *   (`rowCanShareLink` in `DriveView`), and the two surfaces agree. What is NOT a
 *   reason to refuse, and was written down as one: `rowCanShare = !shareBroken`
 *   gates "Share to another Drive", not Share link…, and the "fully inert (Delete
 *   only)" comment sits on the rename/move/copy block.
 */
export function shareLinkOffered(row: ShareLinkRow): boolean {
  if (!row.itemId) return false
  if (row.itemReshareBlocked || row.itemShareBroken) return false
  if (row.itemPointer && (isPendingPointer(row.itemPointer) || isDeadChain(row.itemPointer))) return false
  if (row.itemIsFolder && structuralFolderBlocksShare(structuralFolderKind([row.itemName || '']))) return false
  return true
}

/** What the dialog is open on: the row, plus the path once it has been read. */
export interface ShareLinkTarget {
  id: string
  name: string
  isFolder: boolean
  path: string
}

/**
 * Orders the path lookups behind one explorer's Share link… item.
 *
 * The dialog opens on the row's NAME at once and the real path arrives after a
 * round trip, because opening nothing until it returned left the click with no
 * visible effect. Three things must not happen in that gap, and it takes both a
 * sequence number and the row id to stop all three:
 *
 * - a second Share link… on ANOTHER row has replaced the target, and the slow
 *   first answer must not land on it — B's links under A's name, in the one
 *   dialog whose job is minting public URLs.
 * - the dialog has been closed. A `null` target stays null — never reopen it.
 * - the dialog was closed and reopened ON THE SAME ROW while the first lookup was
 *   still out, and the file MOVED in between. Both answers are for that row, so
 *   an id comparison matches both and the slow first answer would overwrite the
 *   fresh one — the dialog naming the folder the file came from, which is the
 *   same wrong answer this item switched off the cached fetch to avoid.
 *
 * The SEQUENCE NUMBER alone covers all three; the id comparison is
 * belt-and-braces and, at the one call site, unreachable (`begin()` and the
 * `setShareLinkTarget` that opens the dialog are in the same synchronous
 * handler, so a matching token implies a matching id). An earlier docblock here
 * credited the id with the first case and introduced the number only for the
 * third — the wrong way round, and worth saying because the number was deleted
 * once on the belief that the id subsumed it. It does not: `begin` increments, so
 * the newest open holds the highest number and `settle` drops the STALE answer,
 * the opposite of what that deletion claimed.
 */
export function createSharePathLookups() {
  let seq = 0
  return {
    /** Call when the dialog opens; pass the result to `settle`. */
    begin(): number {
      return ++seq
    },
    /** Fold this lookup's answer into the open dialog, or drop it. */
    settle(prev: ShareLinkTarget | null, token: number, itemId: string, path: string): ShareLinkTarget | null {
      if (token !== seq) return prev
      if (!prev || prev.id !== itemId) return prev
      return { ...prev, path }
    },
  }
}

/**
 * What a "Linked from Drives" ROOT row knows about sharing itself, as the workspace
 * links listing reports it.
 *
 * It used to be read from the listing that loads the root's CHILDREN, which is lazy
 * — so before the first expansion the answer was genuinely unknown, the gate had to
 * refuse rather than guess, and "Share link…" appeared on a linked root only after
 * you had expanded it once. The server sends both facts with the link now, so there
 * is no unknown state and no guess: `features/workspace/api/routes.ts` computes them
 * in the loop that already resolves each link's permission (`linkShareFacts`), with
 * the same helpers the file listing uses.
 */
export interface RootShareFacts {
  /** The owner shared this mount without onward-sharing rights — a mint is refused. */
  reshareBlocked?: boolean
  /** The mount is dead — a mint is a hard 403. */
  shareBroken?: boolean
}

// The return type names two fields, so it is an ALLOWLIST, not a coupling: a new
// refusal added to `ShareLinkRow` would not be flagged here. Anything added there
// that a linked ROOT row can carry has to be added here too.
export function linkedRootShareRow(facts: RootShareFacts): Pick<ShareLinkRow, 'itemReshareBlocked' | 'itemShareBroken'> {
  return { itemReshareBlocked: !!facts.reshareBlocked, itemShareBroken: !!facts.shareBroken }
}
